/**
 * Running a local relay server will allow you to hide your API key
 * and run custom logic on the server
 *
 * Set the local relay server address to:
 * REACT_APP_LOCAL_RELAY_SERVER_URL=http://localhost:8081
 *
 * This will also require you to set OPENAI_API_KEY= in a `.env` file
 * You can run it with `npm run relay`, in parallel with `npm start`
 */

import { useEffect, useState } from 'react';
import { Session } from '../agent/session';
import { AgentMessage } from '../agent/agent';
import { CONFIG } from '../config';

import { InputPanel } from '../components/inputpanel/InputPanel';
import { Dialog } from '../components/dialog/Dialog';
import { Overlay } from '../components/overlay/Overlay';

import './ConsolePage.scss';

interface ConsolePageProps {
  sessionRef: React.MutableRefObject<Session>;
}

export function ConsolePage({ sessionRef }: ConsolePageProps) {
  /**
   * All of our variables for displaying application state
   * - items are all conversation items (dialog)
   * - realtimeEvents are event logs, which can be expanded
   */
  const [items, setItems] = useState<AgentMessage[]>([]);
  const [isConnected, setIsConnected] = useState(true);
  const [isTimeout, setIsTimeout] = useState(false);
  const [outputVolume, setOutputVolume] = useState<number>(0);
  const [showInitialOverlay, setShowInitialOverlay] = useState(true);

  /**
   * Disconnect and reset conversation state
   */
  // const disconnectConversation = useCallback(async () => {
  //   setIsConnected(false);
  //   setItems([]);
  //   await sessionRef.current.disconnect();
  // }, []);

  /**
   * Auto-scroll the conversation logs
   */
  useEffect(() => {
    const conversationEls = [].slice.call(
      document.body.querySelectorAll('[data-conversation-content]')
    );
    for (const el of conversationEls) {
      const conversationEl = el as HTMLDivElement;
      conversationEl.scrollTop = conversationEl.scrollHeight;
    }
  }, [items]);

  /**
   * Set up render loop for voice volume
   */
  useEffect(() => {
    let isLoaded = true;
    const session = sessionRef.current;
    const render = () => {
      if (isLoaded) {
        // For the input: session.getInputFrequencyData();
        const result = session.getOutputFrequencyData();
        // Calculate average volume from frequency data
        const avgVolume = result.values.reduce((a, b) => Math.abs(a) + Math.abs(b), 0);
        setOutputVolume(avgVolume);
        window.requestAnimationFrame(render);
      }
    };
    render();
    return () => {
      isLoaded = false;
    };
  }, [sessionRef]);

  useEffect(() => {
    const session = sessionRef.current;
    session.setTextIO({ setItems });
    session.setConnectionStatusCallback((connected) => {
      setIsConnected(connected);
      if (!connected) {
        // We should not have any voice if disconnected.
        sessionRef.current.setMuted(true);
        // Check if the session has been running for the timeout duration
        const sessionStartTime = new Date(session.getStartTime()).getTime();
        const elapsed = Date.now() - sessionStartTime;
        setIsTimeout(elapsed >= CONFIG.CONVERSATION_TIMEOUT_MS);
      }
    });

    return () => {
      session.setTextIO(null);
      session.setConnectionStatusCallback(() => {});
    };
  }, [sessionRef]);

  /**
   * Render the application
   */
  return (
    <div data-component="ConsolePage">
      <Overlay 
        show={showInitialOverlay || !isConnected}
        message={
          showInitialOverlay ? 
          "Welcome to The Gospel of Trump! You have 100 seconds talking with the leader. Tips:\n1. The voice is not muted by default. You can mute it by clicking the speaker icon.\n2. You can type, send your voice and make a phone call. Choose the way you like. Time to start! Enjoy!" :
          isTimeout ? 
          "Time up. Thank you for your trial." : 
          "Mr. Trump is busy. Try to connect him later."
        }
        buttonText={
          showInitialOverlay ? "Got it" :
          isTimeout ? "OK" : "Close"
        }
        isRefresh={isTimeout}
        onClose={() => showInitialOverlay ? setShowInitialOverlay(false) : setIsConnected(true)}
      />
      <div className="content-top">
        <div className="content-title">
          <div className="title">
            <h1>The Gospel of Trump</h1>
          </div>
        </div>
      </div>

      <div className="content-main">
        <div className="content-logs">
          <div className="content-block conversation-fullpage">
            <div className="content-block-body" data-conversation-content>
              {!items.length && (
                <div className="loading-wave">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              )}
              {items.map((conversationItem, i) => {
                if (conversationItem.type === 'message') {
                  const role = conversationItem.data.role;
                  const content = conversationItem.data.content;
                  // Only pass volume to the last assistant message
                  const isLastAssistantMessage = role === 'assistant' &&
                    i === items.reduce((lastIndex, item, currentIndex) =>
                      item.type === 'message' && item.data.role === 'assistant' ? currentIndex : lastIndex, -1);

                  return (
                    <div className="conversation-item" key={conversationItem.id}>
                      <Dialog
                        role={role}
                        content={content}
                        isInterrupted={conversationItem.data.is_interrupted}
                        volume={isLastAssistantMessage ? outputVolume : undefined} />
                    </div>
                  );
                }
                return undefined;
              })}
            </div>
          </div>
          <div className="content-actions">
            <InputPanel
              sessionRef={sessionRef}
              onSubmit={() => { }}
              bottomBar={true}
            />
          </div>
        </div>
      </div>
      <footer className="page-footer">This is an AI character. What is said should not be relied upon as fact or advice. We do not collect, store, or share any user data.</footer>
    </div>
  );
}
